import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { KindOfLossClient, KindOfLossModel, KindOfLossRefModel } from '../../../../../client/ApiClient';
import { DataCell, DataRow, DataTable } from '../../../../basic/DataTable';
import { RangePaginationState, RangePagination, paginate } from 'components/basic/Pagination';
import StageActionToolbar from '../generic/StageActionToolbar';
import { DropdownMenu, DropdownToggle, DropdownItem, UncontrolledDropdown } from 'reactstrap';
import { stagingPageUpdate, stagingPageCopyMappings, StagingPageCopyMappingsItem } from '../generic/Abstractions'
import { RowStatus, AssignRowStatus } from '../generic';
import DataListInput from 'components/basic/DataListInput';
import { ValueChangeEvent } from '../../../../basic/Events';
import { filterColumns, sort } from '../generic/EntityFunctions';

import _ from 'lodash';

import '../../../../../styles/Icons.scss';

interface KindOfLossStagingPageArgs {
    conversionId: string,
}

type KindOfLossStagingPageProps = RouteComponentProps<KindOfLossStagingPageArgs>;

interface KindOfLossStagingPageState {
    items: (KindOfLossModel
        & StagingPageCopyMappingsItem)[];
    destinationCodes: KindOfLossRefModel[];
    filter: string;
    sort: boolean;
    sortColumn: string;
    mapped: boolean;
    notMapped: boolean;

    pagination: RangePaginationState;
    buttonLock: boolean;
    filteredColumn: string;
}

export class KindOfLossStagingPage extends React.Component<KindOfLossStagingPageProps, KindOfLossStagingPageState>{
    private client: KindOfLossClient = new KindOfLossClient()

    private readonly copyMappings = stagingPageCopyMappings(this);

    constructor(props: KindOfLossStagingPageProps) {
        super(props)
        this.state = {
            items: [],
            destinationCodes: [],
            filter: '',
            sort: true,
            sortColumn: '',
            mapped: true,
            notMapped: true,

            pagination: {
                pageSize: 25,
                pageNumber: 0,
            },
            buttonLock: true,
            filteredColumn: '',
        }
    }

    componentDidMount() {
        this.getItems();
    }

    setDefaults = (entity: KindOfLossModel) => new KindOfLossModel({
        ...entity,
        referenceId: entity.referenceId || 0,
        destinationCode: this.destinationName(entity.referenceId, true),
        destinationDescription: this.destinationName(entity.referenceId, false)
    });

    getItems = () => {

        const items = this.client.getAll(this.props.match.params.conversionId, '')
        const refs = this.client.getRef(this.props.match.params.conversionId)
        Promise.all([items, refs])
            .then(result => {
                this.setState({
                    items: result[0]!,
                    destinationCodes: _.sortBy(result[1]!, e => e.sourceCode)
                }, async () => {
                    if (await items && await refs) {
                        this.setState({
                            items: sort(this.state.items.map(this.setDefaults), this.state.sortColumn, this.state.sort, [])
                        });
                    }
                })

            })
            .catch(error => { });
    }

    put(entity: KindOfLossModel) {
        if (entity.referenceId === 0) {
            entity.destinationCode = ''
        }

        stagingPageUpdate(this, this.client)
            .withSource('Kind of Loss', entity.sourceCode!)
            .update(entity)
            .catch(() => { });
    }

    handleSearch = (filter: string) => {
        this.setState({ filter })
    }


    handleChange = (e: ValueChangeEvent<HTMLInputElement, string>, index: number) => {
        const refId = e.value ? Number(e.value) : undefined;
        const id = Number(e.target.id);
        const items = this.state.items;
        const i = this.state.items.findIndex(v => v.id === id);
        items[i].referenceId = refId;
        items[i].destinationCode = this.destinationName(refId, true);
        items[i].destinationDescription = this.destinationName(refId, false);
        this.setState({ items });
        this.put(KindOfLossModel.fromJS(items[i]))
    }

    destinationName = (refId: number | undefined, type: boolean) => {
        if (refId) {
            const destination = this.state.destinationCodes.filter(ref => ref.id === refId);
            if (destination[0]) { return type ? destination[0].sourceCode : destination[0].name }
            else { return '' }
        } else {
            return ''
        }
    }

    sort(column: string) {
        const sorted = this.state.sortColumn === column ? !this.state.sort : true;
        const items = sort(this.state.items, column, sorted, [])
        this.setState({ sort: sorted, sortColumn: column, items });
    }

    mappedCheck(check: string) {
        if (check === 'notMapped') {
            const notMapped = !this.state.notMapped
            this.setState({ notMapped })
        } else if (check === 'mapped') {
            const mapped = !this.state.mapped
            this.setState({ mapped })
        }
    }

    KindOfLossRefs = (): any[] => {
        const arr = [];
        const kindOfLossRefs = this.state.destinationCodes;
        for (const [, kindOfLossRef] of kindOfLossRefs.entries()) {
            arr.push(<option
                key={kindOfLossRef.id}
                data-value={kindOfLossRef.id}
            >
                {`${kindOfLossRef.sourceCode} - ${kindOfLossRef.shortName}`}
            </option>)
        }
        return arr;

    }

    lockButton = (lock: boolean) => {
        this.setState({ buttonLock: lock })
    }

    selectFilteredColumn = (filteredColumn: string) => {
        this.setState({ filteredColumn })
    }

    public render() {
        const conversionId = this.props.match.params.conversionId;
        const filteredColumn = this.state.filteredColumn;
        const items = this.state.items
            .map(AssignRowStatus)
            .filter(i => ((filteredColumn === '' || filteredColumn === 'sourceCode') && i.sourceCode && i.sourceCode!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'sourceDescription') && i.sourceDescription && i.sourceDescription!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'destinationCode') && i.destinationCode && i.destinationCode!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'destinationDescription') && i.destinationDescription && i.destinationDescription!.toLowerCase().includes(this.state.filter.trim().toLowerCase())))
            .filter(item =>
                (item.mapped === RowStatus.NotMapped && this.state.notMapped)
                || (item.mapped === RowStatus.Mapped && this.state.mapped))

        return (
            <>
                <StageActionToolbar
                    conversionId={conversionId}
                    sectionId='kindofloss'
                    handleSearch={this.handleSearch}
                    tierGate='tier4'
                    disableMigrate={false}
                    handleCopyMapping={this.copyMappings.submitMappings}
                    copyMap={true}
                    getItems={() => this.getItems()}
                    filter={this.state.filter}
                    lockButton={this.lockButton}
                    filterColumns={filterColumns(items)}
                    selectFilteredColumn={this.selectFilteredColumn}
                />
                <DataTable>
                    <thead>
                        <tr className='nonFullWidthRow'>
                            <th onClick={() => this.sort('sourceCode')}>Source Code<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('sourceDescription')}>Source Description<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('destinationCode')}>Destination Code<i className="fa fa-sort"></i></th>
                            <th className='checkbox-column'>
                                <UncontrolledDropdown setActiveFromChild className="ismappedDropdown">
                                    <DropdownToggle tag="a" className="ismapped" caret>
                                        Mapped
                                    </DropdownToggle>
                                    <DropdownMenu>
                                        <DropdownItem className={!this.state.notMapped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('notMapped')}>&#x274C; Not Mapped</DropdownItem>
                                        <DropdownItem className={!this.state.mapped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('mapped')}>&#x2705; Mapped</DropdownItem>
                                    </DropdownMenu>
                                </UncontrolledDropdown>
                            </th>
                            <th onClick={() => this.sort('isUserMapped')} className='checkbox-column'>Auto-Mapped<i className="fa fa-sort"></i></th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            paginate(items, this.state.pagination, (item, index) => (
                                <DataRow id={item.id.toString()} key={item.id} tabIndex={-1}>
                                    <DataCell>{item.sourceCode}</DataCell>
                                    <DataCell>{item.sourceDescription}</DataCell>
                                    <DataCell>
                                        <DataListInput
                                            id={item.id.toString()}
                                            value={item.referenceId ? item.referenceId : undefined}
                                            onValueChange={event => this.handleChange(event, index)}
                                            disabled={this.state.buttonLock}
                                        >
                                            <option data-value='' />
                                            {this.KindOfLossRefs()}
                                        </DataListInput>
                                    </DataCell>
                                    <DataCell className='checkbox-column' itemProp={item.mapped === RowStatus.NotMapped ? 'unmapped' : item.mapped === RowStatus.Mapped ? 'mapped' : 'dropped'}>
                                        {item.mapped === RowStatus.NotMapped ? < span className='mapped' itemProp='unmapped'>&#x274C;</span> : item.mapped === RowStatus.Mapped ? < span className='mapped' itemProp='mapped'>&#x2705;</span> : < span className='mapped' itemProp='dropped'>&#x2796;</span>}
                                    </DataCell>
                                    <DataCell
                                        className='checkbox-column'
                                        itemProp={!item.isUserMapped && item.isMapped ? 'auto-mapped' : ''}
                                    >
                                        {!item.isUserMapped && item.isMapped ? < span className='mapped' itemProp='auto-mapped'>&#x2705;</span> : ''}
                                    </DataCell>
                                </DataRow>
                            ))
                        }
                    </tbody>
                </DataTable>
                <RangePagination
                    pageNumber={this.state.pagination.pageNumber}
                    pageSize={this.state.pagination.pageSize}
                    recordsCount={items.length}
                    onChangeIndex={index => this.setState({
                        pagination: {
                            ...this.state.pagination,
                            pageNumber: index,
                        }
                    })}
                />
            </>
        );
    }
}